import {PropsWithChildren, useEffect, useLayoutEffect, useRef, useState} from 'react';
import {MoveRight} from 'lucide-react';
import gsap from 'gsap';
import {ScrambleTextPlugin} from 'gsap/ScrambleTextPlugin';
import {AnimatedBorderCanvas} from './animated-border-canvas';
import {cn, useStaticAssetsUrl} from "@tinct/core-utils";
import {useMaxTextHeight} from '../hooks';
import {AiBlockIcon} from "../../../icon/ai-block-icon";

gsap.registerPlugin(ScrambleTextPlugin);

// Find common prefix length between two strings
function getCommonPrefixLength(a: string, b: string): number {
  let i = 0;
  while (i < a.length && i < b.length && a[i] === b[i]) {
    i++;
  }
  return i;
}

// Find common suffix length between two strings (excluding the prefix)
function getCommonSuffixLength(a: string, b: string, prefixLen: number): number {
  let i = 0;
  const maxSuffix = Math.min(a.length - prefixLen, b.length - prefixLen);
  while (i < maxSuffix && a[a.length - 1 - i] === b[b.length - 1 - i]) {
    i++;
  }
  return i;
}

// Store active tweens and their target text by element
const activeTweens = new WeakMap<HTMLElement, { tween: gsap.core.Tween; targetText: string }>();

// Helper to animate only the differing part of text
function scrambleDiff(
  element: HTMLElement,
  oldText: string,
  newText: string,
  options: { duration: number; chars: string; speed: number }
) {
  // Check if there's an active animation
  const existing = activeTweens.get(element);
  if (existing) {
    // Kill the animation and get current displayed text
    existing.tween.kill();
    // Use current element text (mid-animation state) instead of the stored oldText
    oldText = element.textContent || oldText;
  }

  // Find common prefix and suffix
  const prefixLen = getCommonPrefixLength(oldText, newText);
  const suffixLen = getCommonSuffixLength(oldText, newText, prefixLen);

  const prefix = newText.slice(0, prefixLen);
  const suffix = suffixLen > 0 ? newText.slice(-suffixLen) : '';
  const oldMiddle = oldText.slice(prefixLen, oldText.length - suffixLen || undefined);
  const newMiddle = newText.slice(prefixLen, newText.length - suffixLen || undefined);

  // If texts are identical, do nothing
  if (oldMiddle === newMiddle) {
    element.textContent = newText;
    activeTweens.delete(element);
    return;
  }

  // Measure current dimensions
  const currentWidth = element.offsetWidth;
  const currentHeight = element.offsetHeight;

  // Measure new text dimensions directly on the element
  element.textContent = newText;
  const newWidth = element.offsetWidth;
  const newHeight = element.offsetHeight;
  element.textContent = oldText;

  // Create structure: prefix + middle (animated) + suffix
  element.innerHTML = '';

  const prefixSpan = document.createElement('span');
  prefixSpan.textContent = prefix;

  const middleSpan = document.createElement('span');
  middleSpan.textContent = oldMiddle;

  const suffixSpan = document.createElement('span');
  suffixSpan.textContent = suffix;

  element.appendChild(prefixSpan);
  element.appendChild(middleSpan);
  element.appendChild(suffixSpan);

  // Lock dimensions to max of old and new
  const maxWidth = Math.max(currentWidth, newWidth);
  const maxHeight = Math.max(currentHeight, newHeight);
  element.style.maxWidth = `${maxWidth}px`;
  element.style.height = `${maxHeight}px`;
  element.style.overflow = 'hidden';

  // Animate only the middle span
  const tween = gsap.to(middleSpan, {
    duration: options.duration,
    scrambleText: {
      text: newMiddle,
      chars: options.chars,
      speed: options.speed,
      tweenLength: false,
    },
    onComplete: () => {
      // Flatten back to plain text
      element.textContent = newText;
      element.style.maxWidth = '';
      element.style.height = '';
      element.style.overflow = '';
      activeTweens.delete(element);
    },
  });

  // Store the tween for potential cancellation
  activeTweens.set(element, { tween, targetText: newText });

  return tween;
}

type LandingHeroProps = {
  id: string;
  title: string;
  subTitle: string;
  bullets: string[];
  cta: string;
  color: string;
  allTitles?: string[];
};



type UIFrameBadgeProps = PropsWithChildren<{
  color: string;
  className?: string;
}>;


function UIFrameBadge({ color, className, children }: UIFrameBadgeProps) {
  const badgeRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [dimensions, setDimensions] = useState<{width: number, height: number}>({
    width: 0,
    height: 0,
  });

  useLayoutEffect(() => {
    function updateDimensions() {
      if (containerRef.current) {
        setDimensions({
          width: containerRef.current.offsetWidth,
          height: containerRef.current.offsetHeight,
        });
      }
    }

    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, []);


  return (
    <div className={cn(`md:inline-flex w-full h-full flex-col justify-start items-start`, className)}>
      {/* Frame Label badge */}
      <div
        ref={badgeRef}
        className="-left-2 top-[-34px] absolute inline-flex justify-start items-start gap-1 opacity-0"
      >
        <div
          className="p-0.5 rounded-lg flex justify-start items-center overflow-hidden"
          style={{ backgroundColor: color }}
        >
          <div className="w-4 h-4 p-0.5 bg-indigo-500 rounded-sm flex justify-center items-center gap-2.5">
            <AiBlockIcon className="w-3 h-3 text-white" />
          </div>
          <div className="px-1 flex justify-center items-center">
            <div className="text-foreground text-xs font-normal font-sans leading-4">
              Tailored Value Proposition
            </div>
          </div>
        </div>
      </div>
      {/* Animated border frame using canvas */}
      <div className="shrink relative" ref={containerRef}>
        <AnimatedBorderCanvas
          color={color}
          width={dimensions.width}
          height={dimensions.height}
          radius={8}
          strokeWidth={2}
          badgeRef={badgeRef}
        />
        {children}
      </div>
    </div>
  );
}

export function LandingHero({id, title, subTitle, bullets, cta, color, allTitles = [] }: LandingHeroProps) {
  const titleRef = useRef<HTMLDivElement>(null);
  const subTitleRef = useRef<HTMLDivElement>(null);
  const bulletRefs = useRef<(HTMLDivElement | null)[]>([]);
  const ctaRef = useRef<HTMLDivElement>(null);

  const prevTitleRef = useRef(title);
  const prevSubTitleRef = useRef(subTitle);
  const prevBulletsRef = useRef(bullets);
  const prevCtaRef = useRef(cta);
  const assetsUrl = useStaticAssetsUrl();


  // Measure max heights and apply minHeight to title
  const {measureRef: titleMeasureRef, targetRef: titleTargetRef} = useMaxTextHeight(allTitles);

  // Scramble text animation on title change
  useEffect(() => {
    if (titleRef.current) {
      scrambleDiff(titleRef.current, prevTitleRef.current, title, {
        duration: 0.8,
        chars: 'lowerCase',
        speed: 0.4,
      });
      prevTitleRef.current = title;
    }
  }, [title]);

  // Scramble text animation on subtitle change
  useEffect(() => {
    if (subTitleRef.current) {
      scrambleDiff(subTitleRef.current, prevSubTitleRef.current, subTitle, {
        duration: 0.6,
        chars: 'lowerCase',
        speed: 0.4,
      });
      prevSubTitleRef.current = subTitle;
    }
  }, [subTitle]);

  // Scramble text animation on bullets change
  useEffect(() => {
    bullets.forEach((bullet, index) => {
      const bulletEl = bulletRefs.current[index];
      const prevBullet = prevBulletsRef.current[index] || '';
      if (bulletEl && bullet !== prevBullet) {
        scrambleDiff(bulletEl, prevBullet, bullet, {
          duration: 0.5,
          chars: 'lowerCase',
          speed: 0.5,
        });
      }
    });
    prevBulletsRef.current = bullets;
  }, [bullets]);

  // Scramble text animation on CTA change
  useEffect(() => {
    if (ctaRef.current) {
      scrambleDiff(ctaRef.current, prevCtaRef.current, cta, {
        duration: 0.5,
        chars: 'upperCase',
        speed: 0.5,
      });
      prevCtaRef.current = cta;
    }
  }, [cta]);

  return (
    <div className="self-stretch flex-1 flex flex-col justify-center items-center gap-6 py-12 md:py-16 relative">
      {/* Background layers */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0">
        <img alt="" className="absolute max-w-none object-cover size-full" src={`${assetsUrl}/img/hero-section-bg.png`} />
        <div className="absolute inset-0 bg-linear-to-b from-[rgba(0,14,66,0.7)] to-[rgba(0,126,88,0.7)] mix-blend-hard-light" />
        <div className="absolute inset-0" style={{ backgroundImage: 'linear-gradient(rgb(0, 0, 0) 0%, rgba(0, 0, 0, 0) 100%), linear-gradient(90deg, rgba(0, 0, 0, 0.5) 0%, rgba(0, 0, 0, 0.5) 100%)' }} />
      </div>

      {/* Hidden measurement container */}

      <div className="self-stretch flex flex-col justify-start items-center gap-16 px-8 md:px-24 relative">
        <div className="w-full max-w-4xl relative inline-flex justify-start items-start gap-6">
          <div className="flex-1 inline-flex flex-col justify-start items-start gap-4 md:gap-6">
            {/* Title - matches 2357:28546 (desktop) / 2357:30582 (mobile) */}
            <div className="relative" ref={titleTargetRef}>
              <UIFrameBadge key={`title-${id}`} color={color} >
                <div
                  ref={titleRef}
                  className="relative self-stretch text-white text-[30px] font-normal font-inter leading-[34px] tracking-[-0.8px] md:text-[56px] md:leading-[60px] md:tracking-[-1.4px] max-w-2xl"
                >
                  {title}
                </div>
              </UIFrameBadge>
              <div ref={titleMeasureRef} aria-hidden="true" className="absolute top-0 w-full left-0 h-full  pointer-events-none invisible" />
            </div>



            {/* Description - matches 2357:28547 (desktop) / 2357:30584 (mobile) */}
            <div
              ref={subTitleRef}
              className="self-stretch text-white text-sm font-normal font-inter leading-5 md:text-base md:leading-6 max-w-2xl relative"
            >
              {subTitle}
            </div>

            {/* Highlights - matches 2357:28548 (desktop) / 2357:30585 (mobile) */}
            <div className="self-stretch inline-flex flex-col gap-1 md:flex-row md:gap-6 justify-start items-start">
              {bullets.map((text, index) => (
                <div
                  key={`bullet-${index}`}
                  className="flex justify-start items-center gap-1.5"
                >
                  <MoveRight className="w-5 h-5 md:w-6 md:h-6 text-white" />
                  <div
                    ref={(el) => { bulletRefs.current[index] = el; }}
                    className="text-white text-xs md:text-sm font-semibold font-inter"
                  >
                    {text}
                  </div>
                </div>
              ))}
            </div>

            {/* CTA Button - matches 2357:28558 (desktop) / 2357:30595 (mobile) */}
            <div className="h-11 px-5 py-2 bg-white inline-flex justify-center items-center gap-2">
              <div
                ref={ctaRef}
                className="text-black text-xs font-semibold font-inter uppercase leading-5 tracking-tight"
              >
                {cta}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
